import {
	type FieldValues,
	type UseFormReturn,
	useWatch,
} from "react-hook-form";
import {
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	EXCLUDED_IDENTIFICATION_TYPES,
	isFundLikeToolType,
} from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolType";
import {
	useCommodityIdentificationOptions,
	useHoldingTypeOptions,
} from "web_myplann/Portfolio/Forms/ToolsShared/useToolIdentificationOptions";
import { useCryptocurrencyOptions } from "web_myplann/Portfolio/Helpers/useCryptocurrencyOptions";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";
import { FlatRowInput } from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { isValidIsin } from "web_myplann/Shared/Utils/isin";

type Props = {
	form: UseFormReturn<FieldValues>;
	showToolType?: boolean;
};

const PageResources = Resources.Portfolio.AddTools;
const TOOL_TYPE_LABELS =
	Resources.PortfolioDetail.LifeInsurance.InvestmentTool.Type;

export const InvestmentToolIdentificationFields = ({
	form,
	showToolType = true,
}: Props) => {
	const { t } = useResource();
	const { control, setValue, getValues } = form;

	const [type, identificationType, identification] = useWatch({
		control,
		name: ["type", "identificationType", "identification"],
	});
	const isSaved = getValues("id") !== null;

	const toolTypeOptions = useEnumSelectOptions(
		ClientPortfolioToolTypeEnum,
		TOOL_TYPE_LABELS,
	);
	const identificationTypeOptions = useEnumSelectOptions(
		ClientPortfolioToolIdentificationTypeEnum,
		PageResources.IdentificationType,
		EXCLUDED_IDENTIFICATION_TYPES,
	);
	const holdingTypeOptions = useHoldingTypeOptions();
	const commodityOptions = useCommodityIdentificationOptions();
	const cryptocurrencyOptions = useCryptocurrencyOptions();

	const onTypeChange = (value: ClientPortfolioToolTypeEnum | null) => {
		setValue("identificationType", null);
		setValue("identification", "");
		setValue("holdingType", null);
		if (value === ClientPortfolioToolTypeEnum.Bond) {
			setValue("instructions", []);
		}
	};

	const showIsinWarning =
		identificationType === ClientPortfolioToolIdentificationTypeEnum.Isin &&
		Boolean(identification?.trim()) &&
		!isValidIsin(identification);

	return (
		<>
			{showToolType &&
				(isSaved ? (
					<FlatRowInput
						label={t(PageResources.Fields.toolType)}
						value={
							type
								? t(TOOL_TYPE_LABELS[type as ClientPortfolioToolTypeEnum])
								: ""
						}
						readOnly
					/>
				) : (
					<FlatRowFormSelect
						control={control}
						name="type"
						label={t(PageResources.Fields.toolType)}
						options={toolTypeOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
						onValueChange={onTypeChange}
					/>
				))}
			{isFundLikeToolType(type) && (
				<>
					<FlatRowFormSelect
						control={control}
						name="identificationType"
						label={t(PageResources.Fields.identificationType)}
						options={identificationTypeOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					/>
					<FlatRowFormInput
						control={control}
						name="identification"
						label={t(PageResources.Fields.identification)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					/>
					{showIsinWarning && (
						<FlatRowHint text={t(PageResources.isinWarning)} />
					)}
				</>
			)}
			{type === ClientPortfolioToolTypeEnum.Crypto && (
				<>
					<FlatRowFormSelect
						control={control}
						name="holdingType"
						label={t(PageResources.Fields.holdingType)}
						options={holdingTypeOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					/>
					<FlatRowFormSelect
						control={control}
						name="identification"
						label={t(PageResources.Fields.cryptocurrency)}
						options={cryptocurrencyOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					/>
				</>
			)}
			{type === ClientPortfolioToolTypeEnum.Commodity && (
				<FlatRowFormCombobox
					control={control}
					name="identification"
					label={t(PageResources.Fields.commodity)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(PageResources.commoditySearch)}
					options={commodityOptions}
				/>
			)}
		</>
	);
};
